export interface LegalBlock {
  heading?: string;
  paragraphs?: string[];
  items?: string[];
}

export interface LegalSection {
  id: 'imprint' | 'privacy' | 'cookies' | 'credits';
  title: string;
  intro?: string;
  blocks: LegalBlock[];
}

export const legalUpdated = 'November 2, 2026';

/** Imprint details, shown as a label / value list */
export const imprintDetails = [
  { label: 'Company', value: 'Immersive Studios' },
  { label: 'Address', value: 'Höhenweg 10, 73479 Ellwangen, Germany' },
  { label: 'Executive director', value: 'Julius Branding' },
  { label: 'Email', value: 'hey@immersivestudios.de', href: 'mailto:hey@immersivestudios.de' },
  { label: 'VAT ID (§ 27 a UStG)', value: 'DE 359 490232' },
  { label: 'Parent company', value: 'Syne — syne.digital', href: 'https://syne.digital/' },
];

/** 3D model credits */
export const modelCredits = [
  {
    work: 'Drone',
    workUrl: 'https://sketchfab.com/3d-models/drone-764eea3376134998bd49bb8e5f7d795e',
    author: 'FeelsBadMan',
    authorUrl: 'https://sketchfab.com/FeelsBadMan',
    license: 'CC BY 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by/4.0/',
    note: 'Used as the base model for Pixel, our on-site companion. Modified: new face display, materials, animation and rigging.',
  },
];

export const legalSections: LegalSection[] = [
  {
    id: 'imprint',
    title: 'Imprint',
    intro: 'Information according to § 5 DDG (German Digital Services Act):',
    blocks: [
      {
        heading: 'Sales tax identification number',
        paragraphs: ['Sales tax identification number according to § 27 a Value Added Tax Act: DE 359 490232'],
      },
      {
        heading: 'Online dispute resolution',
        paragraphs: [
          'Platform of the EU Commission for online dispute resolution: https://ec.europa.eu/odr',
          'We are neither obligated nor willing to participate in a dispute resolution procedure before a consumer arbitration board.',
        ],
      },
      {
        heading: 'Responsibility for content',
        paragraphs: [
          'We create the content of this website with great care. However, we cannot guarantee that it is complete, accurate or up to date at all times. As a service provider, we are responsible for our own content on these pages under general law.',
        ],
      },
      {
        heading: 'Links to other websites',
        paragraphs: [
          'Our website contains links to external websites over whose content we have no control. The respective provider or operator is always responsible for the content of linked pages. If we become aware of any legal violations, we will remove such links immediately.',
        ],
      },
      {
        heading: 'Copyright',
        paragraphs: [
          'All content, designs, images, videos and texts on this website are the property of Immersive Studios or its clients and partners, unless stated otherwise. Reproduction, editing or distribution outside the limits of copyright law requires our prior written consent. Third-party content is marked as such — see Credits below.',
        ],
      },
      {
        heading: 'Website credits',
        paragraphs: [
          'Design, Creative Direction and Development by Julius Branding.',
          '100% made with AI in Germany.',
        ],
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    intro:
      'This policy explains which personal data we process when you visit this website or get in touch with us, for what purposes and on which legal basis we do so, how long we keep it, who receives it and which rights you have. We process personal data in accordance with the EU General Data Protection Regulation (GDPR), the German Federal Data Protection Act (BDSG) and the German Telecommunications Digital Services Data Protection Act (TDDDG). “Personal data” means any information relating to an identified or identifiable natural person, such as your name, email address or IP address.',
    blocks: [
      {
        heading: '1. Controller',
        paragraphs: [
          'The controller responsible for processing personal data on this website within the meaning of Art. 4(7) GDPR is:',
          'Immersive Studios, Executive Director: Julius Branding, Höhenweg 10, 73479 Ellwangen, Germany. Email: hey@immersivestudios.de',
          'The controller is the person who alone or jointly with others decides on the purposes and means of processing personal data. For any question about data protection, or to exercise your rights, simply write to us at the address above.',
        ],
      },
      {
        heading: '2. Data protection officer',
        paragraphs: [
          'We are not legally required to appoint a data protection officer (Art. 37 GDPR, § 38 BDSG), as we do not permanently employ ten or more people in the automated processing of personal data and our core activities do not consist of large-scale or particularly sensitive processing. Data protection is handled directly by the executive director. Please send all privacy enquiries to hey@immersivestudios.de.',
        ],
      },
      {
        heading: '3. Overview: what we process and why',
        paragraphs: ['In short, we process personal data only where this is necessary to:'],
        items: [
          'Deliver this website to you securely, quickly and reliably',
          'Display fonts, images, videos and the interactive 3D character correctly',
          'Answer your enquiries and prepare or carry out projects with you',
          'Comply with our legal obligations, for example tax and commercial retention duties',
          'Defend against attacks and misuse, and to assert or defend legal claims',
        ],
      },
      {
        paragraphs: [
          'We do not sell personal data, we do not use it for advertising profiles, we do not track you across other websites and we do not use automated decision-making, including profiling, within the meaning of Art. 22 GDPR.',
        ],
      },
      {
        heading: '4. Legal bases',
        paragraphs: ['Depending on the purpose, we rely on the following legal bases:'],
        items: [
          'Art. 6(1)(a) GDPR — your consent, which you can withdraw at any time with effect for the future',
          'Art. 6(1)(b) GDPR — performance of a contract or pre-contractual measures taken at your request, e.g. answering a project enquiry',
          'Art. 6(1)(c) GDPR — compliance with legal obligations, e.g. statutory retention periods under German tax and commercial law',
          'Art. 6(1)(f) GDPR — our legitimate interests, e.g. the secure and efficient operation of this website, provided your interests or fundamental rights do not override them',
          '§ 25(1) TDDDG — your consent to storing or accessing information on your device, where it is not strictly necessary',
          '§ 25(2) No. 2 TDDDG — storing or accessing information on your device where this is strictly necessary to provide the service you have expressly requested',
        ],
      },
      {
        heading: '5. Hosting and server log files',
        paragraphs: [
          'This website is hosted by an external hosting provider. Whenever you open a page, your browser automatically transmits technical information to the provider’s servers, which is stored temporarily in so-called server log files. This includes:',
        ],
        items: [
          'Your IP address (and the approximate location that can be derived from it)',
          'Date and time of the request, and the time zone difference to GMT',
          'The page or file requested and the amount of data transferred',
          'The referring website (referrer URL), if transmitted',
          'Browser type and version, operating system and device type',
          'HTTP status code (e.g. whether the request succeeded)',
        ],
      },
      {
        paragraphs: [
          'This data is required to deliver the website to your device, to keep it stable and to detect, analyse and defend against attacks (e.g. denial-of-service attempts). The legal basis is Art. 6(1)(f) GDPR; our legitimate interest lies in the technically error-free and secure provision of our website. The data is not merged with other data sources and is not used to identify you personally.',
          'Log files are deleted automatically after a short period, at the latest after 30 days, unless longer storage is required in an individual case to clarify or prosecute a specific security incident. Our hosting provider processes this data on our behalf on the basis of a data processing agreement pursuant to Art. 28 GDPR and only according to our instructions.',
        ],
      },
      {
        heading: '6. Encryption (TLS)',
        paragraphs: [
          'For security reasons and to protect the transmission of content, this website uses TLS encryption (HTTPS). You can recognise an encrypted connection by “https://” in the address bar and the lock symbol in your browser. When encryption is active, the data you transmit to us cannot be read by third parties.',
        ],
      },
      {
        heading: '7. Contacting us by email',
        paragraphs: [
          'If you contact us by email (for example via one of the contact buttons on this site), we process the data you provide — usually your name, email address, company, telephone number if given, and the content of your message — in order to handle your enquiry and any follow-up questions.',
          'If your enquiry relates to the conclusion or performance of a contract, the legal basis is Art. 6(1)(b) GDPR. In all other cases, the legal basis is our legitimate interest in effectively processing enquiries addressed to us (Art. 6(1)(f) GDPR). Providing your data is voluntary; however, without it we cannot answer your enquiry.',
          'We delete enquiries as soon as they have been dealt with and the purpose of storage no longer applies, unless statutory retention obligations apply. Business correspondence relevant to a contract is retained for six years (§ 257 German Commercial Code, HGB), and documents relevant for tax purposes for up to ten years (§ 147 German Fiscal Code, AO).',
          'Please note that ordinary email is not end-to-end encrypted and may be readable during transmission. We therefore recommend that you do not send highly sensitive information by unencrypted email. If you need a secure channel, let us know and we will agree one with you.',
        ],
      },
      {
        heading: '8. Clients and business partners',
        paragraphs: [
          'When we work with you on a project, we process the contact and communication data of you and your staff, project and billing data, and any content you provide to us, in order to perform the contract (Art. 6(1)(b) GDPR), to comply with our legal obligations (Art. 6(1)(c) GDPR) and to manage our business relationship (Art. 6(1)(f) GDPR). Where we process personal data on your behalf as part of a project, we conclude a separate data processing agreement with you.',
        ],
      },
      {
        heading: '9. Chatting with Pixel',
        paragraphs: [
          'Pixel, the animated robot on this website, runs entirely in your browser. His conversations are pre-written: the answers you click are not sent to us, to an AI service or to any other third party, and no chat history is stored. Pixel reacts to your mouse, touch and scroll movements only locally on your device in order to animate him; these movements are not recorded or transmitted.',
        ],
      },
      {
        heading: '10. Fonts, media and 3D content from external providers',
        paragraphs: [
          'To display this website consistently and with good performance, your browser loads certain files — fonts, images, videos and the 3D model of Pixel together with the software that decodes it — from external servers. For this to work, your browser must connect to these servers, which means your IP address and the technical request data described in section 5 are transmitted to the respective provider. We use:',
        ],
        items: [
          'Google Fonts and Google static content (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland; parent company Google LLC, USA) — for web fonts and the 3D model decoder',
          'Fontshare (Indian Type Foundry) — for the “Clash Display” display typeface',
          'Vercel (Vercel Inc., USA) — for hosting media and 3D files',
          'The content delivery network of our website platform — for images and videos',
        ],
      },
      {
        paragraphs: [
          'The legal basis is our legitimate interest in a uniform, fast and appealing presentation of our website (Art. 6(1)(f) GDPR); insofar as information is accessed on your device, this is strictly necessary to display the website you have requested (§ 25(2) No. 2 TDDDG). The providers receive only the technical data needed to deliver the files. We have no influence on how long they keep this data; please refer to their privacy policies for details.',
        ],
      },
      {
        heading: '11. Transfers to countries outside the EU',
        paragraphs: [
          'Some of the providers mentioned above are based in, or may process data in, the United States or other countries outside the European Union / European Economic Area. Such transfers take place only if the special requirements of Art. 44 et seq. GDPR are met — in particular on the basis of an adequacy decision of the European Commission, such as the EU–US Data Privacy Framework for certified companies, or of the EU Standard Contractual Clauses (Art. 46(2)(c) GDPR) together with additional safeguards where necessary.',
          'Please note that in third countries, authorities may under certain circumstances be able to access personal data, and that the level of protection there may not fully match that of the EU.',
        ],
      },
      {
        heading: '12. Social media links',
        paragraphs: [
          'Our website contains simple links to our profiles on Instagram, LinkedIn and X. These are ordinary text links, not plug-ins or embedded buttons: no data is transmitted to the platforms when you merely view our website. Only when you click a link are you taken to the respective platform, which then processes your data under its own responsibility and in accordance with its own privacy policy.',
        ],
      },
      {
        heading: '13. Recipients of personal data',
        paragraphs: [
          'Within our studio, only those people who need your data to fulfil the purposes described above have access to it. Beyond that, we pass data on only where this is permitted by law — in particular to service providers acting on our behalf under Art. 28 GDPR (such as hosting, email and IT providers), to tax advisers and auditors who are bound to professional secrecy, and to public authorities where we are legally obliged to do so.',
        ],
      },
      {
        heading: '14. Storage period',
        paragraphs: [
          'Unless a more specific storage period is stated in this policy, we keep personal data only for as long as it is needed for the purpose for which it was collected. Once that purpose ceases, the data is deleted or restricted, unless statutory retention periods (in particular under § 257 HGB and § 147 AO) or the establishment, exercise or defence of legal claims require longer storage. In that case, we restrict processing to these purposes and delete the data once the periods have expired.',
        ],
      },
      {
        heading: '15. Your rights',
        paragraphs: ['Under the GDPR you have the following rights with regard to your personal data:'],
        items: [
          'Right of access (Art. 15 GDPR) — to know whether and which of your data we process',
          'Right to rectification (Art. 16 GDPR) — to have inaccurate or incomplete data corrected',
          'Right to erasure (Art. 17 GDPR) — to have your data deleted, unless legal obligations require us to keep it',
          'Right to restriction of processing (Art. 18 GDPR) — for example while the accuracy of your data is being checked',
          'Right to data portability (Art. 20 GDPR) — to receive data you provided in a structured, machine-readable format',
          'Right to withdraw consent (Art. 7(3) GDPR) — at any time, with effect for the future; processing up to the withdrawal remains lawful',
          'Right to lodge a complaint (Art. 77 GDPR) — with a data protection supervisory authority',
        ],
      },
      {
        heading: 'Right to object (Art. 21 GDPR)',
        paragraphs: [
          'Where we process your data on the basis of our legitimate interests (Art. 6(1)(f) GDPR), you have the right to object to this processing at any time on grounds relating to your particular situation. We will then no longer process your data unless we can demonstrate compelling legitimate grounds that override your interests, rights and freedoms, or the processing serves the establishment, exercise or defence of legal claims. If we were ever to process your data for direct marketing, you may object to this at any time without giving reasons, and we will then stop such processing.',
        ],
      },
      {
        heading: 'Supervisory authority',
        paragraphs: [
          'You can lodge a complaint with any data protection supervisory authority, in particular in the EU member state of your habitual residence or place of work. The authority responsible for us is: Der Landesbeauftragte für den Datenschutz und die Informationsfreiheit Baden-Württemberg (State Commissioner for Data Protection and Freedom of Information Baden-Württemberg), Lautenschlagerstraße 20, 70173 Stuttgart, Germany — www.baden-wuerttemberg.datenschutz.de',
          'To exercise any of your rights, an informal email to hey@immersivestudios.de is sufficient. We will respond without undue delay and in any event within one month (Art. 12(3) GDPR). To protect your data, we may ask you to confirm your identity before we disclose any information.',
        ],
      },
      {
        heading: '16. Children',
        paragraphs: [
          'This website is aimed at businesses and adults. We do not knowingly collect personal data from children under the age of 16. If you believe a child has sent us personal data, please contact us and we will delete it.',
        ],
      },
      {
        heading: '17. Security',
        paragraphs: [
          'We take appropriate technical and organisational measures in line with Art. 32 GDPR to protect your data against accidental or intentional manipulation, loss, destruction and unauthorised access — including encrypted transmission, access restrictions, careful selection of our service providers and regular review of our measures in line with technological developments. Please note, however, that no transmission over the internet can be guaranteed to be completely secure.',
        ],
      },
      {
        heading: '18. Changes to this policy',
        paragraphs: [
          'We may update this privacy policy when our website, our services or the legal situation change. The version published on this page always applies. Where changes require your consent, we will ask for it before they take effect.',
        ],
      },
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies',
    intro:
      'Cookies are small text files stored on your device. Similar technologies, such as your browser’s local storage, work in a comparable way. Here is how we use them.',
    blocks: [
      {
        heading: 'What we use',
        paragraphs: [
          'This website currently uses only technically necessary storage — for example, to remember your settings within a single visit or to make animations and scrolling work smoothly. This does not require consent (§ 25(2) TDDDG). We do not use advertising cookies, and we do not track you across other websites.',
        ],
      },
      {
        heading: 'Analytics and marketing',
        paragraphs: [
          'We do not currently use analytics or marketing cookies. If we introduce them in the future, we will only set them after you have given your consent (§ 25 TDDDG, Art. 6(1)(a) GDPR), and we will update this page beforehand.',
        ],
      },
      {
        heading: 'Third-party content',
        paragraphs: [
          'When files such as fonts, images or videos are loaded from external providers (see Privacy Policy, section 5), those providers may use technically necessary cookies or similar technologies to deliver them.',
        ],
      },
      {
        heading: 'Managing cookies',
        paragraphs: [
          'You can view, block or delete cookies at any time in your browser settings. Blocking technically necessary storage may affect how parts of the website work.',
        ],
      },
    ],
  },
  {
    id: 'credits',
    title: 'Credits',
    intro: 'Some elements of this website are based on third-party work. Thank you to the creators.',
    blocks: [],
  },
];
